import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {boolean} */
  #isClosing = false;

  /** @type {number | undefined} */
  #closeFallbackTimer;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.refs.dialog?.addEventListener('cancel', this.#handleCancel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.refs.dialog?.removeEventListener('cancel', this.#handleCancel);
    window.clearTimeout(this.#closeFallbackTimer);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    window.clearTimeout(this.#closeFallbackTimer);
    this.#isClosing = false;
    dialog?.classList.remove('is-closing');
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    const { dialog } = /** @type {Refs} */ (this.refs);

    if (!dialog?.open || this.#isClosing) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.closeDialog();
      return;
    }

    this.#isClosing = true;
    dialog.classList.add('is-closing');

    const finishClose = () => {
      if (!this.#isClosing) return;
      window.clearTimeout(this.#closeFallbackTimer);
      dialog.removeEventListener('animationend', handleAnimationEnd);
      this.#isClosing = false;
      this.closeDialog();
      dialog.classList.remove('is-closing');
    };

    const handleAnimationEnd = (event) => {
      if (event.target === dialog && event.animationName === 'flavor-cart-slide-out') {
        finishClose();
      }
    };

    dialog.addEventListener('animationend', handleAnimationEnd);
    this.#closeFallbackTimer = window.setTimeout(finishClose, 450);
  }

  #handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
